import { z } from "zod";
import { openRecoveryBackup, unwrapFromPrivateKey, wrapToPublicKey, importPublicKey, base64ToBytes, bytesToBase64, publicKeyFingerprint, recoveryBackupSchema, recoveryProof, unwrapPrivateKey, deriveKeyFromPassword, type RecoveryBackup } from "@fc/core";
import { createKeyBundle, openGroupKey, sealGroupKeyFor, ownPublicKey, proveDeviceKey, type KeyBundle } from "./keyring.js";
import { trpcClient } from "./trpc.js";

export const helpRequestSchema = z.object({
  format: z.literal("myfriends-help-v1"), ticket: z.string().max(30000),
  fingerprint: z.string().max(80), username: z.string().max(32),
});
export const helpApprovalSchema = z.object({ format: z.literal("myfriends-approval-v1"), approval: z.string().max(30000) });
export type PendingRecovery = { ticket: string; challenge: string; fingerprint: string; keys?: KeyBundle; proof?: string };

export function downloadRecoveryFile(filename: string, value: unknown): void {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2) + "\n"], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url; anchor.download = filename; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export async function readRecoveryFile(file: File): Promise<RecoveryBackup> {
  if (file.size > 40_000) throw new Error("That is not a recovery file.");
  return recoveryBackupSchema.parse(JSON.parse(await file.text()));
}
export async function readSmallJson(file: File): Promise<unknown> {
  if (file.size > 40_000) throw new Error("That recovery file is too large.");
  return JSON.parse(await file.text());
}
export async function backupStamp(backup: RecoveryBackup): Promise<string> {
  const data = new TextEncoder().encode(`${backup.wrappedPrivateKey}:${backup.salt}`);
  return [...new Uint8Array(await crypto.subtle.digest("SHA-256", data))].map((b) => b.toString(16).padStart(2, "0")).join("");
}
export async function beginFriendRecovery(username: string, password: string): Promise<PendingRecovery> {
  const keys = await createKeyBundle(password);
  const started = await trpcClient.recovery.begin.mutate({ username, publicKey: keys.publicKey, newPublicKey: keys.publicKey, method: "friend" });
  const derived = await deriveKeyFromPassword(password, base64ToBytes(keys.keySalt), keys.kdfIterations);
  const proof = await recoveryProof(started.challenge, await unwrapPrivateKey(keys.wrappedPrivateKey, derived));
  return { ...started, keys, proof };
}
export async function beginDeviceRecovery(username: string, password: string) {
  const publicKey = ownPublicKey();
  if (!publicKey) throw new Error("This device cannot open your photos. Use a recovery file or ask a friend.");
  const keys = await createKeyBundle(password);
  const started = await trpcClient.recovery.begin.mutate({ username, publicKey, newPublicKey: keys.publicKey, method: "device" });
  const proof = await proveDeviceKey(started.challenge);
  const existing = await trpcClient.recovery.deviceGrants.query();
  const grants = [];
  for (const grant of existing) {
    await openGroupKey(grant.groupId, grant.wrappedGroupKey);
    grants.push({ groupId: grant.groupId, wrappedGroupKey: await sealGroupKeyFor(grant.groupId, keys.publicKey) });
  }
  return { ...started, keys, proof, grants };
}
export { publicKeyFingerprint, bytesToBase64 };

/** Restore old group grants locally; neither the backup code nor private key reaches the API. */
export async function restorePhotosFromFile(file: File, userId: string, username: string): Promise<number> {
  const backup = await readRecoveryFile(file);
  if (backup.userId !== userId) throw new Error("This recovery file belongs to a different account.");
  const publicKey = ownPublicKey();
  if (!publicKey) throw new Error("Sign in again before restoring photos.");
  const oldKey = await openRecoveryBackup(backup);
  const recipient = await importPublicKey(publicKey);
  const groups = await trpcClient.recovery.progress.query({});
  const grants = [];
  for (const group of groups) {
    if (!group.wrappedGroupKey) continue;
    try { await openGroupKey(group.groupId, group.wrappedGroupKey); continue; } catch { /* Try the older backup next. */ }
    try {
      const raw = await unwrapFromPrivateKey(group.wrappedGroupKey, oldKey);
      grants.push({ groupId: group.groupId, expectedWrappedGroupKey: group.wrappedGroupKey, wrappedGroupKey: await wrapToPublicKey(raw, recipient) });
    } catch { /* A different key generation needs its own backup or a member's approval. */ }
  }
  if (!grants.length) return 0;
  const started = await trpcClient.recovery.begin.mutate({ username, publicKey, method: "photos" });
  const result = await trpcClient.recovery.restoreBackupPhotos.mutate({ ticket: started.ticket, proof: await proveDeviceKey(started.challenge), grants });
  return result.restored;
}
